import { POLLED_DASHBOARD_NOTIFICATION_KEYS } from './dashboard-notifications'

/**
 * The store's ROOT state, actions and mutations - the handful of things that belong to the whole
 * app rather than to any one module: the sidebar, and the notification banner across the top of
 * the dashboard.
 *
 * Split out of store/index.js so it can be composed into a store of two modules in a test.
 * store/index.js constructs the real store, which imports every module, every api and the router
 * behind them, and none of that has anything to say about which banner is showing.
 */
export const rootState = () => ({
  isSidebarExpanded: true,

  isDashboardNotificationVisible: false,

  dashboardNotificationMessageDetails: {
    text: '',
    linkText: '',
    routeName: '',
    hideOnClick: true
  }
})

export const rootActions = {
  toggleSidebar({ commit }) {
    commit('TOGGLE_SIDEBAR')
  },

  showDashboardNotification({ commit }, messageDetails) {
    commit('SET_DASHBOARD_NOTIFICATION_VISIBILITY', { show: true, messageDetails })
  },

  hideDashboardNotification({ commit }) {
    commit('SET_DASHBOARD_NOTIFICATION_VISIBILITY', { show: false, messageDetails: '' })
  },

  /**
   * Put up, replace, or take back down one of the POLLED notifications - see
   * `dashboard-notifications.js`. `null` means the account is fine now.
   *
   * That last half is the one the banner never had. Low balance used to be a single
   * `showDashboardNotification` at the end of `getBuyerAccountBalance`, dispatched only when the
   * flag was true, from the dashboard route's `beforeEnter` - which Vue Router does not re-run when
   * the agent moves between that route's children. So the banner appeared on a page refresh and on
   * no other occasion, and once up it stayed up through the top-up that fixed it.
   *
   * The slot is shared, and the two directions are deliberately NOT symmetric.
   *
   * SHOWING takes the slot from whatever is in it. An account that cannot buy calls outranks
   * anything else the dashboard has to say, and last-writer-wins is what the slot did before this
   * existed anyway. Once it has taken it the key is ours, so the poll recognises its own work and
   * stops re-committing.
   *
   * HIDING only ever takes down one of OURS. Everything else in the slot was raised once by
   * something that is not watching the account and would never put it back.
   *
   * This started out refusing to write over anyone else's notification, which was the wrong
   * instinct and quietly defeated the whole change: `fetchBuyerCampaign` raises a keyless
   * "campaign daily budget is exceeded" with `hideOnClick: false`, which the banner renders with an
   * INVISIBLE close button - so nothing could lower it and nothing could replace it, and a buyer
   * who had opened such a campaign never saw a low balance banner again for the rest of the session.
   */
  syncPolledDashboardNotification({ commit, state }, messageDetails) {
    const visibleKey = state.isDashboardNotificationVisible
      ? state.dashboardNotificationMessageDetails?.key
      : null

    if (!messageDetails) {
      if (visibleKey && POLLED_DASHBOARD_NOTIFICATION_KEYS.includes(visibleKey)) {
        commit('SET_DASHBOARD_NOTIFICATION_VISIBILITY', { show: false, messageDetails: {} })
      }

      return
    }

    // Already saying exactly this. Re-committing it on every poll would churn the slot, and with it
    // every component reading the banner, for no change at all.
    if (visibleKey === messageDetails.key) {
      return
    }

    commit('SET_DASHBOARD_NOTIFICATION_VISIBILITY', { show: true, messageDetails })
  }
}

export const rootMutations = {
  TOGGLE_SIDEBAR(state) {
    state.isSidebarExpanded = !state.isSidebarExpanded
  },

  SET_DASHBOARD_NOTIFICATION_VISIBILITY(state, { show, messageDetails }) {
    state.isDashboardNotificationVisible = show
    state.dashboardNotificationMessageDetails = messageDetails
  },

  reset() {
    return {}
  }
}
